/**
 * CaptureEditModal.js — D.5 pending-chip Edit flow.
 *
 * Opens when the user clicks Edit on a pending CaptureChip. Lets them
 * adjust entity_name, predicate, and the predicate-specific value payload,
 * then POSTs to /api/chat/capture/edit/{id} (assertion written at 0.90,
 * per Rule 105).
 *
 * Save-state machine:
 *
 *       [onEdit from chip]
 *              │
 *              ▼
 *   ┌──────────────────────┐  Cancel/ESC/backdrop    ┌──────────────────┐
 *   │  modal:editing       ├────────────────────────▶│  modal:closed    │
 *   │  (Save dynamically   │   (no API call)         │  chip stays pend │
 *   │   gated by required) │                         └──────────────────┘
 *   └──────────┬───────────┘
 *              │ Save (required fields present)
 *              ▼
 *   ┌──────────────────────┐
 *   │  modal:saving        │  POST editCandidate
 *   │  (Save+Cancel disab) │
 *   └──────────┬───────────┘
 *              │
 *   ┌──────────┼──────────────┬──────────────────┐
 *  200        409            400              5xx / network
 *   │          │              │                  │
 *   ▼          ▼              ▼                  ▼
 * onSave…   on409            error banner      error banner
 * (parent   (parent flips    stay open,        stay open,
 *  flips    chip to          Save re-enabled,  Save re-enabled,
 *  chip to  already_saved    log [..-400]      log [..-5xx/NETWORK]
 *  captured and closes)
 *  and
 *  closes)
 *
 * Canonical value schemas (modal is the ONLY enforcement point — backend
 * stores whatever JSON it receives). Required fields per schema:
 *
 *   price_change           : direction + (amount_pct XOR amount_usd).
 *                            Optional: timing, item.
 *   lead_time_change       : from_weeks, to_weeks.
 *                            Optional: direction, item.
 *   capacity_constraint    : fill_rate_pct OR constraint_type.
 *                            Optional: item.
 *   sourcing_strategy_change: from, to.
 *                            Optional: added_supplier.
 *   demand_signal          : magnitude_pct, direction.
 *                            Optional: timeframe.
 *   quality_issue          : defect_pct OR lot.
 *                            Optional: item.
 *   contact_change         : old_contact OR new_contact.
 *                            Optional: role.
 *   material_substitution  : from, to.
 *
 * Predicate-switch resets value to {} (Decision 2c — cleaner than trying to
 * preserve overlap keys, since the new predicate's required schema is
 * almost always disjoint from the old one's).
 *
 * Rule 108 discipline: all state is owned by this component. The Save
 * Promise is awaited only inside handleSave; no parent hot path awaits it.
 * Home unmount mid-save is guarded by isMountedRef — the server-side write
 * still completes (candidate + assertion persist), client just doesn't
 * setState on a torn-down tree.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import CaptureChip from './CaptureChip';
import { colors } from './AppLayout';
import { formatChipLabel } from '../utils/captureLabel';
import { editCandidate } from '../api/chatCapture';


const PREDICATE_OPTIONS = [
  { value: 'price_change', label: 'Price change' },
  { value: 'lead_time_change', label: 'Lead time change' },
  { value: 'capacity_constraint', label: 'Capacity constraint' },
  { value: 'sourcing_strategy_change', label: 'Sourcing strategy change' },
  { value: 'demand_signal', label: 'Demand signal' },
  { value: 'quality_issue', label: 'Quality issue' },
  { value: 'contact_change', label: 'Contact change' },
  { value: 'material_substitution', label: 'Material substitution' },
];


// ============================================================================
// Required-field logic — XOR satisfied when EITHER side is non-empty.
// ============================================================================

function hasStr(v) {
  return typeof v === 'string' && v.trim().length > 0;
}

function hasNum(v) {
  if (v === '' || v === null || v === undefined) return false;
  return Number.isFinite(Number(v));
}

function requiredSatisfied(predicate, value, priceUnit) {
  const v = value || {};
  switch (predicate) {
    case 'price_change': {
      if (v.direction !== 'up' && v.direction !== 'down') return false;
      return priceUnit === 'usd' ? hasNum(v.amount_usd) : hasNum(v.amount_pct);
    }
    case 'lead_time_change':
      return hasNum(v.from_weeks) && hasNum(v.to_weeks);
    case 'capacity_constraint':
      return hasNum(v.fill_rate_pct) || hasStr(v.constraint_type);
    case 'sourcing_strategy_change':
      return hasStr(v.from) && hasStr(v.to);
    case 'demand_signal':
      if (v.direction !== 'up' && v.direction !== 'down') return false;
      return hasNum(v.magnitude_pct);
    case 'quality_issue':
      return hasNum(v.defect_pct) || hasStr(v.lot);
    case 'contact_change':
      return hasStr(v.old_contact) || hasStr(v.new_contact);
    case 'material_substitution':
      return hasStr(v.from) && hasStr(v.to);
    default:
      return false;
  }
}

function requiredHint(predicate) {
  switch (predicate) {
    case 'price_change':
      return 'Amount and direction are required.';
    case 'lead_time_change':
      return 'From and to weeks are required.';
    case 'capacity_constraint':
      return 'Fill in at least one: fill rate or constraint type.';
    case 'sourcing_strategy_change':
      return 'From and to are required.';
    case 'demand_signal':
      return 'Magnitude and direction are required.';
    case 'quality_issue':
      return 'Fill in at least one: defect % or lot.';
    case 'contact_change':
      return 'Fill in at least one: old contact or new contact.';
    case 'material_substitution':
      return 'From and to are required.';
    default:
      return '';
  }
}


// ============================================================================
// Shared field primitives (inline style, mirrors CaptureChip conventions)
// ============================================================================

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  border: `1px solid ${colors.mediumGray}`,
  borderRadius: '6px',
  fontSize: '13px',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: colors.darkText,
  boxSizing: 'border-box',
  background: colors.white,
};

const labelStyle = {
  display: 'block',
  fontSize: '11px',
  textTransform: 'uppercase',
  fontWeight: 600,
  letterSpacing: '0.06em',
  color: colors.textMuted,
  marginBottom: '4px',
};

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={labelStyle}>{label}</div>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder }) {
  return (
    <input
      type="text"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={inputStyle}
    />
  );
}

function NumberInput({ value, onChange, placeholder }) {
  return (
    <input
      type="number"
      step="any"
      value={value ?? ''}
      onChange={(e) => {
        const raw = e.target.value;
        // Preserve undefined for empty so requiredSatisfied's hasNum() rejects it.
        onChange(raw === '' ? undefined : Number(raw));
      }}
      placeholder={placeholder}
      style={inputStyle}
    />
  );
}

function DirectionSelect({ value, onChange }) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value || undefined)}
      style={inputStyle}
    >
      <option value="">None</option>
      <option value="up">up</option>
      <option value="down">down</option>
    </select>
  );
}


// ============================================================================
// Per-predicate sub-forms — each takes { value, onValueChange, ... } and
// renders only the fields for that predicate's canonical schema.
// ============================================================================

function PriceChangeForm({ value, onValueChange, priceUnit, onPriceUnitChange }) {
  const set = (k, nv) => onValueChange({ ...value, [k]: nv });
  return (
    <>
      <Field label="Units">
        <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: colors.darkText }}>
          <label style={{ cursor: 'pointer' }}>
            <input
              type="radio"
              name="priceUnit"
              checked={priceUnit === 'pct'}
              onChange={() => {
                onPriceUnitChange('pct');
                onValueChange({ ...value, amount_usd: undefined });
              }}
              style={{ marginRight: '6px' }}
            />
            % change
          </label>
          <label style={{ cursor: 'pointer' }}>
            <input
              type="radio"
              name="priceUnit"
              checked={priceUnit === 'usd'}
              onChange={() => {
                onPriceUnitChange('usd');
                onValueChange({ ...value, amount_pct: undefined });
              }}
              style={{ marginRight: '6px' }}
            />
            $ change
          </label>
        </div>
      </Field>
      {priceUnit === 'pct' ? (
        <Field label="Amount (%)">
          <NumberInput value={value.amount_pct} onChange={(v) => set('amount_pct', v)} placeholder="8" />
        </Field>
      ) : (
        <Field label="Amount ($)">
          <NumberInput value={value.amount_usd} onChange={(v) => set('amount_usd', v)} placeholder="2500" />
        </Field>
      )}
      <Field label="Direction">
        <DirectionSelect value={value.direction} onChange={(v) => set('direction', v)} />
      </Field>
      <Field label="Timing (optional)">
        <TextInput value={value.timing} onChange={(v) => set('timing', v)} placeholder="effective Jan 1" />
      </Field>
      <Field label="Item (optional)">
        <TextInput value={value.item} onChange={(v) => set('item', v)} placeholder="O-ring" />
      </Field>
    </>
  );
}

function LeadTimeChangeForm({ value, onValueChange }) {
  const set = (k, nv) => onValueChange({ ...value, [k]: nv });
  return (
    <>
      <Field label="From (weeks)">
        <NumberInput value={value.from_weeks} onChange={(v) => set('from_weeks', v)} placeholder="6" />
      </Field>
      <Field label="To (weeks)">
        <NumberInput value={value.to_weeks} onChange={(v) => set('to_weeks', v)} placeholder="10" />
      </Field>
      <Field label="Direction (optional)">
        <DirectionSelect value={value.direction} onChange={(v) => set('direction', v)} />
      </Field>
      <Field label="Item (optional)">
        <TextInput value={value.item} onChange={(v) => set('item', v)} placeholder="tungsten seals" />
      </Field>
    </>
  );
}

function CapacityConstraintForm({ value, onValueChange }) {
  const set = (k, nv) => onValueChange({ ...value, [k]: nv });
  return (
    <>
      <Field label="Fill rate (%), or enter constraint type">
        <NumberInput value={value.fill_rate_pct} onChange={(v) => set('fill_rate_pct', v)} placeholder="70" />
      </Field>
      <Field label="Constraint type, or fill rate above">
        <TextInput value={value.constraint_type} onChange={(v) => set('constraint_type', v)} placeholder="allocation" />
      </Field>
      <Field label="Item (optional)">
        <TextInput value={value.item} onChange={(v) => set('item', v)} placeholder="FKM" />
      </Field>
    </>
  );
}

function SourcingStrategyForm({ value, onValueChange }) {
  const set = (k, nv) => onValueChange({ ...value, [k]: nv });
  return (
    <>
      <Field label="From">
        <TextInput value={value.from} onChange={(v) => set('from', v)} placeholder="single-source" />
      </Field>
      <Field label="To">
        <TextInput value={value.to} onChange={(v) => set('to', v)} placeholder="dual-source" />
      </Field>
      <Field label="Added supplier (optional)">
        <TextInput value={value.added_supplier} onChange={(v) => set('added_supplier', v)} placeholder="Vulcan" />
      </Field>
    </>
  );
}

function DemandSignalForm({ value, onValueChange }) {
  const set = (k, nv) => onValueChange({ ...value, [k]: nv });
  return (
    <>
      <Field label="Magnitude (%)">
        <NumberInput value={value.magnitude_pct} onChange={(v) => set('magnitude_pct', v)} placeholder="20" />
      </Field>
      <Field label="Direction">
        <DirectionSelect value={value.direction} onChange={(v) => set('direction', v)} />
      </Field>
      <Field label="Timeframe (optional)">
        <TextInput value={value.timeframe} onChange={(v) => set('timeframe', v)} placeholder="last quarter" />
      </Field>
    </>
  );
}

function QualityIssueForm({ value, onValueChange }) {
  const set = (k, nv) => onValueChange({ ...value, [k]: nv });
  return (
    <>
      <Field label="Defect (%), or enter lot">
        <NumberInput value={value.defect_pct} onChange={(v) => set('defect_pct', v)} placeholder="30" />
      </Field>
      <Field label="Lot, or defect % above">
        <TextInput value={value.lot} onChange={(v) => set('lot', v)} placeholder="Hastelloy lot 0417" />
      </Field>
      <Field label="Item (optional)">
        <TextInput value={value.item} onChange={(v) => set('item', v)} placeholder="valve seats" />
      </Field>
    </>
  );
}

function ContactChangeForm({ value, onValueChange }) {
  const set = (k, nv) => onValueChange({ ...value, [k]: nv });
  return (
    <>
      <Field label="Old contact, or new contact below">
        <TextInput value={value.old_contact} onChange={(v) => set('old_contact', v)} placeholder="Mike" />
      </Field>
      <Field label="New contact, or old contact above">
        <TextInput value={value.new_contact} onChange={(v) => set('new_contact', v)} placeholder="Sarah" />
      </Field>
      <Field label="Role (optional)">
        <TextInput value={value.role} onChange={(v) => set('role', v)} placeholder="account rep" />
      </Field>
    </>
  );
}

function MaterialSubstitutionForm({ value, onValueChange }) {
  const set = (k, nv) => onValueChange({ ...value, [k]: nv });
  return (
    <>
      <Field label="From">
        <TextInput value={value.from} onChange={(v) => set('from', v)} placeholder="NBR" />
      </Field>
      <Field label="To">
        <TextInput value={value.to} onChange={(v) => set('to', v)} placeholder="Viton" />
      </Field>
    </>
  );
}

const SUBFORMS = {
  price_change: PriceChangeForm,
  lead_time_change: LeadTimeChangeForm,
  capacity_constraint: CapacityConstraintForm,
  sourcing_strategy_change: SourcingStrategyForm,
  demand_signal: DemandSignalForm,
  quality_issue: QualityIssueForm,
  contact_change: ContactChangeForm,
  material_substitution: MaterialSubstitutionForm,
};


// ============================================================================
// Modal
// ============================================================================

function CaptureEditModal({
  open,
  candidateId,
  initialPayload,
  onClose,
  onSaveSuccess,
  on409,
}) {
  const [entityName, setEntityName] = useState('');
  const [predicate, setPredicate] = useState('price_change');
  const [value, setValue] = useState({});
  const [priceUnit, setPriceUnit] = useState('pct');
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Re-seed state every time the modal opens with a (possibly new) candidate.
  useEffect(() => {
    if (!open || !initialPayload) return;
    setEntityName(initialPayload.entity_name || '');
    const p = PREDICATE_OPTIONS.some((o) => o.value === initialPayload.predicate)
      ? initialPayload.predicate
      : 'price_change';
    setPredicate(p);
    const v = initialPayload.value && typeof initialPayload.value === 'object' ? initialPayload.value : {};
    setValue(v);
    if (p === 'price_change') {
      const hasUsd = hasNum(v.amount_usd);
      const hasPct = hasNum(v.amount_pct);
      setPriceUnit(hasUsd && !hasPct ? 'usd' : 'pct');
    } else {
      setPriceUnit('pct');
    }
    setErrorMsg(null);
    setSaving(false);
  }, [open, initialPayload, candidateId]);

  // ESC to close (when not saving).
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, saving, onClose]);

  const handlePredicateChange = (e) => {
    setPredicate(e.target.value);
    setValue({}); // Decision 2c — reset on predicate change.
    setPriceUnit('pct');
  };

  const livePayload = useMemo(() => ({
    entity_type: initialPayload?.entity_type || null,
    entity_name: entityName,
    predicate,
    value,
  }), [initialPayload, entityName, predicate, value]);

  const previewLabel = useMemo(() => formatChipLabel(livePayload), [livePayload]);

  const canSave =
    !saving
    && entityName.trim().length > 0
    && requiredSatisfied(predicate, value, priceUnit);

  let helperText = '';
  if (!canSave && !saving) {
    if (entityName.trim().length === 0) helperText = 'Entity name required.';
    else helperText = requiredHint(predicate);
  }

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setErrorMsg(null);
    // Strip undefined / empty-string keys so backend stores a clean dict.
    const cleanValue = Object.fromEntries(
      Object.entries(value).filter(([, v]) => v !== undefined && v !== '')
    );
    const edits = {
      entity_name: entityName.trim(),
      predicate,
      value: cleanValue,
    };
    try {
      await editCandidate(candidateId, edits);
      if (!isMountedRef.current) return;
      onSaveSuccess({
        entity_type: initialPayload?.entity_type || null,
        entity_name: entityName.trim(),
        predicate,
        value: cleanValue,
      });
    } catch (err) {
      if (!isMountedRef.current) return;
      if (err?.status === 409) {
        console.log('[CHAT-CAPTURE-EDIT-409]', candidateId);
        on409();
        return;
      }
      if (err?.status === 400) {
        console.log('[CHAT-CAPTURE-EDIT-400]', err.detail);
        setErrorMsg("Couldn't save: invalid edit.");
      } else if (typeof err?.status === 'number' && err.status >= 500) {
        console.log('[CHAT-CAPTURE-EDIT-5xx]', err.status, err.detail);
        setErrorMsg("Couldn't save. Try again.");
      } else {
        console.log('[CHAT-CAPTURE-EDIT-NETWORK]', err?.message || String(err));
        setErrorMsg('Network error. Try again.');
      }
      setSaving(false);
    }
  };

  if (!open) return null;

  const SubForm = SUBFORMS[predicate] || null;

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="capture-edit-modal-title"
        style={{
          background: colors.white,
          borderRadius: '12px',
          width: '90%',
          maxWidth: '560px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: `1px solid ${colors.mediumGray}`,
          flexShrink: 0,
        }}>
          <h2
            id="capture-edit-modal-title"
            style={{ margin: 0, fontSize: '16px', color: colors.darkText, fontWeight: 600 }}
          >
            Edit capture
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '22px',
              color: colors.textMuted,
              cursor: saving ? 'not-allowed' : 'pointer',
              padding: 0,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px', overflowY: 'auto' }}>
          <Field label="Entity name">
            <TextInput value={entityName} onChange={setEntityName} placeholder="Parker" />
          </Field>

          <Field label="Predicate">
            <select
              value={predicate}
              onChange={handlePredicateChange}
              style={inputStyle}
            >
              {PREDICATE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>

          {SubForm && (
            <SubForm
              value={value}
              onValueChange={setValue}
              priceUnit={priceUnit}
              onPriceUnitChange={setPriceUnit}
            />
          )}

          {/* Live preview */}
          <div style={{
            marginTop: '16px',
            paddingTop: '12px',
            borderTop: `1px solid ${colors.mediumGray}`,
          }}>
            <div style={labelStyle}>Preview</div>
            <div style={{ marginTop: '6px' }}>
              <CaptureChip variant="captured" label={previewLabel} />
            </div>
          </div>

          {errorMsg && (
            <div style={{
              marginTop: '12px',
              padding: '8px 12px',
              background: '#FEE2E2',
              border: `1px solid ${colors.danger}`,
              borderRadius: '6px',
              color: '#7F1D1D',
              fontSize: '13px',
            }}>
              {errorMsg}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 20px',
          borderTop: `1px solid ${colors.mediumGray}`,
          flexShrink: 0,
          gap: '12px',
        }}>
          <span style={{
            fontSize: '12px',
            color: colors.textMuted,
            flex: 1,
            minWidth: 0,
          }}>
            {helperText}
          </span>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{
                background: 'transparent',
                border: `1px solid ${colors.mediumGray}`,
                color: colors.darkText,
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              style={{
                background: canSave ? colors.electricBlue : colors.mediumGray,
                border: `1px solid ${canSave ? colors.electricBlue : colors.mediumGray}`,
                color: canSave ? colors.white : colors.textMuted,
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: canSave ? 'pointer' : 'not-allowed',
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CaptureEditModal;
